import { useEffect, useRef, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { twMerge } from 'tailwind-merge';
import { useShallow } from 'zustand/react/shallow';

import { CurrencyAccountSelect } from '@/features/account-select';

import { CountrySelect } from '@/entities/country';
import { useAccountStore } from '@/entities/currency-account/model/store';
import { PaymentSystemSelect } from '@/entities/payment-systems/ui';

import {
  VabsAchAccountType,
  VabsParticipantType,
  VabsPaymentSystem,
  VabsWireAccountType,
} from '@/shared/api';
import { Plus } from '@/shared/assets';
import {
  Button,
  DashboardFormMessage,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  Typography,
} from '@/shared/ui';
import {
  VabsAchAccountTypeSelect,
  VabsWireAccountTypeSelect,
} from '@/shared/ui/vabs-acctype-select';

import {
  SendMoneyInfoStepData,
  sendMoneyInfoSchema,
} from '../../../model/schema_new';
import { useSendMoney } from '../../../model/useSendMoney';
import { getFieldName } from '../send-money-shared/lib';

export const InfoStepForm = () => {
  const { setStep, updateFormData, data } = useSendMoney(
    useShallow((state) => state),
  );
  const defaultAcc = useAccountStore((state) => state.account);
  const [hasBankForm, setHasBankForm] = useState(false);
  const form = useForm<SendMoneyInfoStepData>({
    defaultValues: {
      account: defaultAcc,
      type: VabsParticipantType.B2C, //TODO: combine b2b and b2c flow
      paymentSystem: null,
      accountNumber: '',
      bankCode: '',
      accType: undefined,
      firstName: '',
      lastName: '',
      companyName: '',
      intermediaryBank: undefined,
    },
    resolver: zodResolver(sendMoneyInfoSchema),
    shouldFocusError: false,
    mode: 'onChange',
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // const scrollToBottom = () => {
  //   if (scrollContainerRef.current) {
  //     const scrollContainer = scrollContainerRef.current;
  //     scrollContainer.scroll({
  //       top: scrollContainer.scrollHeight,
  //       behavior: 'smooth',
  //     });
  //   }
  // };

  useEffect(() => {
    if (data?.accountNumber) {
      form.setValue('account', data.account);
      form.setValue('accountNumber', data.accountNumber);
      form.setValue('bankCode', data.bankCode);
      form.setValue('paymentSystem', data.paymentSystem);
      form.setValue('accType', data.accType);
      form.setValue('firstName', data.firstName);
      form.setValue('lastName', data.lastName);
      if (data?.intermediaryBank) {
        setHasBankForm(true);
        form.setValue('intermediaryBank', data.intermediaryBank);
      }
    }
  }, []);
  const formData = useWatch({
    control: form.control,
  });

  const onSubmit = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setStep('bank');
      updateFormData({
        ...formData,
      });
    }
  };

  return (
    <div className="mt-5 flex-1">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)} //DOES NOT WORK
        >
          <div className="relative flex flex-col gap-y-1">
            <div className="mb-3 flex flex-col gap-y-1">
              <FormField
                control={form.control}
                name="account"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CurrencyAccountSelect
                        selected={field.value}
                        onSelect={(account) => {
                          updateFormData({ account });
                          form.resetField('paymentSystem');
                          form.resetField('accType');
                          field.onChange(account);
                        }}
                        title="Choose a currency"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="paymentSystem"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PaymentSystemSelect
                        key={formData.account?.currencyId}
                        currency={formData.account?.currencyId}
                        participant={VabsParticipantType.B2C}
                        onSelect={(i) => {
                          updateFormData({ paymentSystem: i.id });
                          field.onChange(i.id);
                          form.resetField('intermediaryBank');
                          form.resetField('accountNumber');
                          form.resetField('bankCode');
                          form.resetField('accType');
                          setHasBankForm(false);
                        }}
                        selected={
                          field.value && {
                            id: field.value,
                            label: field.value.toUpperCase(),
                          }
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            {!formData.paymentSystem ? (
              <div className="h-[200px]"></div>
            ) : (
              <>
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className="w-full"
                          classNames={{ input: '!typography-16r' }}
                          id="first-name"
                          placeholder="First and middle name"
                          type="text"
                          {...field}
                        />
                      </FormControl>
                      <DashboardFormMessage>
                        {form.formState.errors.firstName?.message}
                      </DashboardFormMessage>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className="w-full"
                          classNames={{ input: '!typography-16r' }}
                          id="last-name"
                          placeholder="Last name"
                          type="text"
                          {...field}
                        />
                      </FormControl>
                      <DashboardFormMessage>
                        {form.formState.errors.lastName?.message}
                      </DashboardFormMessage>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="accountNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className="w-full"
                          classNames={{ input: '!typography-16r' }}
                          id="acc-no"
                          placeholder={getFieldName(
                            formData.paymentSystem,
                            'accountNumber',
                          )}
                          type="text"
                          {...field}
                        />
                      </FormControl>
                      <DashboardFormMessage>
                        {form.formState.errors.accountNumber?.message}
                      </DashboardFormMessage>
                    </FormItem>
                  )}
                />
                <div className={twMerge("flex items-start gap-1", formData.paymentSystem === VabsPaymentSystem.Bsb && 'mb-[18px]')}>
                  <FormField
                    control={form.control}
                    name="bankCode"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControl>
                          <Input
                            className="w-full"
                            classNames={{ input: '!typography-16r' }}
                            id="bank-code"
                            placeholder={getFieldName(
                              formData.paymentSystem,
                              'bankCode',
                            )}
                            type="text"
                            {...field}
                          />
                        </FormControl>
                        <DashboardFormMessage>
                          {form.formState.errors.bankCode?.message}
                        </DashboardFormMessage>
                      </FormItem>
                    )}
                  />
                </div>

                {(formData.paymentSystem === VabsPaymentSystem.Ach ||
                  formData.paymentSystem === VabsPaymentSystem.Wire) && (
                    <div className="flex items-start w-full gap-1 mb-[18px]">
                      <FormField
                        control={form.control}
                        name="accType"
                        render={({ field }) => (
                          <FormItem className="w-full">
                            <FormControl>
                              {formData.paymentSystem === VabsPaymentSystem.Ach ? (
                                <VabsAchAccountTypeSelect
                                  value={field.value as VabsAchAccountType}
                                  onSelect={field.onChange}
                                  errorText={form.formState.errors.accType?.message}
                                />
                              ) : (
                                <VabsWireAccountTypeSelect
                                  value={field.value as VabsWireAccountType}
                                  onSelect={field.onChange}
                                  errorText={form.formState.errors.accType?.message}
                                />
                              )}
                            </FormControl>
                            <DashboardFormMessage>
                              {form.formState.errors.accType?.message}
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                {formData.paymentSystem === VabsPaymentSystem.Swift && (
                  <Button
                    className={twMerge(
                      'mx-2 mb-[43px] mt-[18px] justify-start',
                      hasBankForm && 'mb-[18px]',
                    )}
                    variant="clean"
                    onClick={(e) => {
                      e.preventDefault();
                      setHasBankForm((prev) => !prev);
                    }}
                  >
                    {!hasBankForm && <Plus />}
                    <Typography variant="14-r">
                      {!hasBankForm
                        ? 'Add intermediary bank'
                        : 'Intermediary bank'}
                    </Typography>
                    {hasBankForm && <span>-</span>}
                  </Button>
                )}
                {hasBankForm && (
                  <>
                    <div className="mb-3 flex flex-col gap-y-1">
                      <FormField
                        control={form.control}
                        name="intermediaryBank.accountNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                className="w-full"
                                classNames={{ input: '!typography-16r' }}
                                id="int-acc"
                                placeholder="Bank account number"
                                type="text"
                                {...field}
                              />
                            </FormControl>
                            <DashboardFormMessage>
                              {
                                form.formState.errors.intermediaryBank
                                  ?.accountNumber?.message
                              }
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="intermediaryBank.swift"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                className="w-full"
                                classNames={{ input: '!typography-16r' }}
                                id="int-swift"
                                placeholder="Bank SWIFT code"
                                type="text"
                                {...field}
                              />
                            </FormControl>
                            <DashboardFormMessage>
                              {
                                form.formState.errors.intermediaryBank?.swift
                                  ?.message
                              }
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="intermediaryBank.bankInfo.name"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                className="w-full"
                                classNames={{ input: '!typography-16r' }}
                                id="int-bank"
                                placeholder="Bank name"
                                type="text"
                                {...field}
                              />
                            </FormControl>
                            <DashboardFormMessage>
                              {
                                form.formState.errors.intermediaryBank?.bankInfo
                                  ?.name?.message
                              }
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="relative mb-8 flex flex-col gap-y-1">
                      <FormField
                        control={form.control}
                        name="intermediaryBank.bankInfo.address.country"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <CountrySelect
                                classNames={{ input: '!typography-16r' }}
                                placeholder="Country"
                                value={field.value as ICountry}
                                onSelect={field.onChange}
                                prefixQueryKey="int-bank"
                                isInvalid={
                                  !!form.formState.errors.intermediaryBank
                                    ?.bankInfo?.address?.country
                                }
                              />
                            </FormControl>
                            <DashboardFormMessage>
                              {
                                form.formState.errors.intermediaryBank?.bankInfo
                                  ?.address?.country?.message
                              }
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="intermediaryBank.bankInfo.address.street"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                className="w-full"
                                classNames={{ input: '!typography-16r' }}
                                id="bank-street"
                                placeholder="Address"
                                type="text"
                                {...field}
                              />
                            </FormControl>
                            <DashboardFormMessage>
                              {
                                form.formState.errors.intermediaryBank?.bankInfo
                                  ?.address?.street?.message
                              }
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="intermediaryBank.bankInfo.address.postcode"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                className="w-full"
                                classNames={{ input: '!typography-16r' }}
                                id="bank-postcode"
                                placeholder="Postcode"
                                type="text"
                                {...field}
                              />
                            </FormControl>
                            <DashboardFormMessage>
                              {
                                form.formState.errors.intermediaryBank?.bankInfo
                                  ?.address?.postcode?.message
                              }
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="intermediaryBank.bankInfo.address.city"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                className="w-full"
                                classNames={{ input: '!typography-16r' }}
                                id="bank-city"
                                placeholder="City"
                                type="text"
                                {...field}
                              />
                            </FormControl>
                            <DashboardFormMessage>
                              {
                                form.formState.errors.intermediaryBank?.bankInfo
                                  ?.address?.city?.message
                              }
                            </DashboardFormMessage>
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>

        </form>
      </Form>
      <Button
        className="w-full sticky bottom-1"
        size="lg"
        type="submit"
        variant="brand-500"
        onClick={onSubmit}
      >
        Continue
      </Button>
    </div>
  );
};
